import React, { Component, ErrorInfo, ReactNode } from 'react';
import { environment } from '../environment';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    
    // Log error to monitoring service in production
    if (import.meta.env.PROD) {
      console.error('Error Boundary caught an error:', error, errorInfo);
      
      // Send to error reporting service if configured
      if (environment.REACT_APP_ENABLE_ERROR_REPORTING) {
        // Integration with Sentry or similar service would go here
        console.log('Error reported to monitoring service');
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: 'var(--surface)',
          color: 'var(--text-primary)'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--error)' }}>
            Something went wrong
          </h1>
          <p style={{ marginBottom: '2rem', color: 'var(--text-muted)' }}>
            We apologize for the inconvenience. An unexpected error occurred.
          </p>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ 
              marginBottom: '2rem', 
              padding: '1rem', 
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '8px',
              textAlign: 'left',
              maxWidth: '600px'
            }}>
              <summary style={{ cursor: 'pointer', color: 'var(--accent-primary)' }}>
                Error Details
              </summary>
              <pre style={{ 
                marginTop: '1rem', 
                fontSize: '0.8rem', 
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap',
                overflow: 'auto'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          
          <div>
            <button
              onClick={this.handleReset}
              style={{
                padding: '0.8rem 1.5rem',
                marginRight: '1rem',
                backgroundColor: 'var(--accent-primary)',
                color: 'var(--surface)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.8rem 1.5rem',
                backgroundColor: 'transparent',
                color: 'var(--accent-primary)',
                border: '1px solid #64ffda',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
